import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Table, Spinner, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { runbookContactActions } from "../../actions/runbookContact.actions";
import { FaPlus, FaEdit, FaTrash, FaBell } from "react-icons/fa";

const FREQUENCY_OPTIONS = [
  { value: "All", label: "All Executions" },
  { value: "ErrorsOnly", label: "Errors Only" },
];

function EmailNotificationModal({ show, onHide, runbook, user }) {
  const dispatch = useDispatch();
  const runbookContactReducer = useSelector((state) => state.runbookContact);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    emailAddress: "",
    frequency: "All",
  });
  const [errors, setErrors] = useState({});

  // Fetch contacts when modal opens
  useEffect(() => {
    if (show && runbook) {
      refreshContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, runbook]);

  // Handle create/update/delete success - refresh the list
  useEffect(() => {
    if (runbookContactReducer.createSuccess ||
        runbookContactReducer.updateSuccess ||
        runbookContactReducer.deleteSuccess) {
      refreshContacts();
      setShowAddForm(false);
      setEditingContact(null);
      setFormData({ emailAddress: "", frequency: "All" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    runbookContactReducer.createSuccess,
    runbookContactReducer.updateSuccess,
    runbookContactReducer.deleteSuccess,
  ]);

  const refreshContacts = () => {
    if (runbook) {
      dispatch(
        runbookContactActions.get({
          runbookId: runbook.runbookId,
          pageSize: 100,
          currentPage: 0,
        })
      );
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.emailAddress || formData.emailAddress.trim() === "") {
      newErrors.emailAddress = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = "Invalid email format";
    }

    if (!formData.frequency) {
      newErrors.frequency = "Frequency is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validate()) return;

    const requestBody = {
      runbookId: runbook.runbookId,
      emailAddress: formData.emailAddress,
      frequency: formData.frequency,
      clientId: user?.clientId,
      clientGuid: user?.clientGuid,
    };

    dispatch(runbookContactActions.create(requestBody));
  };

  const handleUpdate = () => {
    if (!validate()) return;

    const requestBody = {
      runbookId: runbook.runbookId,
      emailAddress: editingContact.emailAddress,
      frequency: formData.frequency,
      clientId: user?.clientId,
      clientGuid: user?.clientGuid,
    };

    dispatch(runbookContactActions.update(requestBody));
  };

  const handleDelete = (contact) => {
    if (window.confirm(`Remove ${contact.emailAddress} from notifications?`)) {
      dispatch(
        runbookContactActions.remove({
          runbookId: runbook.runbookId,
          emailAddress: contact.emailAddress,
          clientId: user?.clientId,
          clientGuid: user?.clientGuid,
        })
      );
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      emailAddress: contact.emailAddress,
      frequency: contact.frequency,
    });
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingContact(null);
    setFormData({ emailAddress: "", frequency: "All" });
    setErrors({});
  };

  const getFrequencyBadge = (frequency) => {
    switch (frequency) {
      case "All":
        return <Badge bg="success">All Executions</Badge>;
      case "ErrorsOnly":
        return <Badge bg="danger">Errors Only</Badge>;
      // No longer a selectable option (Inspect Request now logs to the admin-only
      // Inspect Requests page instead of emailing), but existing contacts created
      // before this change can still carry it - keep the friendly label for those.
      case "InspectRequest":
        return <Badge bg="warning" text="dark">Inspect Request (legacy)</Badge>;
      default:
        return <Badge bg="secondary">{frequency}</Badge>;
    }
  };

  if (!runbook) {
    return null;
  }

  const contacts = runbookContactReducer.paginatedResult?.data || [];
  const isAdmin = user?.isAdmin || user?.isSuperUser;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaBell className="me-2 mb-1" />
          Email Notifications - {runbook.runbookId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {runbookContactReducer.loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {!runbookContactReducer.loading && (
          <>
            {/* Contact List */}
            {contacts.length > 0 ? (
              <Table hover size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th>Email Address</th>
                    <th>Frequency</th>
                    {isAdmin && <th style={{ width: "100px" }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.emailAddress}>
                      <td>{contact.emailAddress}</td>
                      <td>{getFrequencyBadge(contact.frequency)}</td>
                      {isAdmin && (
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 me-2"
                            onClick={() => handleEdit(contact)}
                            title="Edit frequency"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            onClick={() => handleDelete(contact)}
                            title="Remove"
                            disabled={runbookContactReducer.loadingDelete}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info">
                No email notifications configured for this runbook.
              </Alert>
            )}

            {/* Add/Edit Form */}
            {isAdmin && showAddForm && (
              <div className="border rounded p-3 mb-3">
                <h6 className="mb-3">
                  {editingContact ? "Edit Frequency" : "Add Email Notification"}
                </h6>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) =>
                      setFormData({ ...formData, emailAddress: e.target.value })
                    }
                    isInvalid={!!errors.emailAddress}
                    disabled={!!editingContact}
                    placeholder="user@example.com"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.emailAddress}
                  </Form.Control.Feedback>
                  {editingContact && (
                    <Form.Text className="text-muted">
                      Email address cannot be changed. Delete and recreate to change.
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Notification Frequency</Form.Label>
                  <Form.Select
                    value={formData.frequency}
                    onChange={(e) =>
                      setFormData({ ...formData, frequency: e.target.value })
                    }
                    isInvalid={!!errors.frequency}
                  >
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                    {/* Editing a legacy contact (e.g. frequency="InspectRequest") whose
                        value isn't one of the current options - show it so the select
                        doesn't render blank, without offering it for new selections. */}
                    {editingContact &&
                      !FREQUENCY_OPTIONS.some((option) => option.value === formData.frequency) && (
                        <option value={formData.frequency}>{formData.frequency} (legacy)</option>
                      )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.frequency}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={editingContact ? handleUpdate : handleAdd}
                    disabled={
                      runbookContactReducer.loadingCreate ||
                      runbookContactReducer.loadingUpdate
                    }
                  >
                    {runbookContactReducer.loadingCreate ||
                    runbookContactReducer.loadingUpdate ? (
                      <Spinner animation="border" size="sm" />
                    ) : editingContact ? (
                      "Update"
                    ) : (
                      "Add"
                    )}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Error Messages */}
            {runbookContactReducer.createError && (
              <Alert variant="danger" className="mt-2">
                Failed to add email notification. Contact may already exist.
              </Alert>
            )}
            {runbookContactReducer.updateError && (
              <Alert variant="danger" className="mt-2">
                Failed to update notification frequency.
              </Alert>
            )}
            {runbookContactReducer.deleteError && (
              <Alert variant="danger" className="mt-2">
                Failed to remove email notification.
              </Alert>
            )}

            {/* Add Button */}
            {isAdmin && !showAddForm && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                <FaPlus className="me-1" /> Add Email Notification
              </Button>
            )}

            {!isAdmin && (
              <Alert variant="warning" className="mt-2">
                Only administrators can manage email notifications.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EmailNotificationModal;
